import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { ClientSidebar } from "@/components/sidebars";
import { useAuth } from "@/lib/auth";
import { apiClient } from "@/lib/api";

const fmtVenc = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return null; }
};

const daysUntil = (iso) => {
  if (!iso) return null;
  try {
    const diff = new Date(iso).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  } catch { return null; }
};

export default function ClientDashboard() {
  const { user, refresh } = useAuth();
  const [solicitudes, setSolicitudes] = useState([]);
  const [porEvaluar, setPorEvaluar] = useState([]);

  useEffect(() => {
    refresh();
    apiClient.get("/solicitudes").then(({ data }) => setSolicitudes(data)).catch(() => {});
    apiClient.get("/me/solicitudes-por-evaluar").then(({ data }) => setPorEvaluar(data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusBadge = user?.membership_status === "active" ? "active" : "pending";
  const beneficiariosCount = user?.beneficiarios?.length || 0;
  const vencFmt = fmtVenc(user?.membership_active_until);
  const diasRestantes = daysUntil(user?.membership_active_until);
  const renewalCancelled = !!user?.renewal_cancelled;

  return (
    <AppLayout sidebar={ClientSidebar}>
      <header className="app-header">
        <h1>Hola, {user?.nombre?.split(" ")[0] || "tu Ángel está aquí"}</h1>
        <p className="text-muted">Gestiona tu membresía y solicita servicio para tu familia en México.</p>
      </header>

      {statusBadge === "active" && vencFmt && (
        <div
          data-testid="banner-vencimiento"
          style={{
            background: renewalCancelled
              ? "linear-gradient(90deg, #FEE2E2 0%, #FECACA 100%)"
              : "linear-gradient(90deg, #E8F5D9 0%, #D1F0B5 100%)",
            border: `1px solid ${renewalCancelled ? "#DC2626" : "#7DC242"}`,
            borderRadius: 12,
            padding: "14px 20px",
            marginBottom: 20,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div>
            <strong style={{ color: renewalCancelled ? "#7F1D1D" : "#3F6212", fontSize: 15 }}>
              <i className={`fas ${renewalCancelled ? "fa-triangle-exclamation" : "fa-shield-check"}`} style={{ marginRight: 8 }} />
              Membresía activa hasta el {vencFmt}
              {diasRestantes !== null && <span style={{ opacity: 0.75, fontWeight: 500 }}> · {diasRestantes} días restantes</span>}
            </strong>
            <div style={{ color: renewalCancelled ? "#991B1B" : "#4D7C0F", fontSize: 12, marginTop: 4 }}>
              {renewalCancelled
                ? "Renovación cancelada. Tu servicio termina en la fecha mostrada."
                : "12 meses efectivos de servicio + 30 días de gracia (13 meses de cobertura total)."}
            </div>
          </div>
          <Link
            to="/cuenta/plan"
            data-testid="banner-vencimiento-cta"
            style={{
              background: renewalCancelled ? "#7F1D1D" : "#163680",
              color: "white",
              padding: "10px 18px",
              borderRadius: 8,
              textDecoration: "none",
              fontWeight: 700,
              fontSize: 13,
            }}
          >
            {renewalCancelled ? "Reactivar renovación" : "Administrar plan →"}
          </Link>
        </div>
      )}

      {porEvaluar.length > 0 && (
        <div
          data-testid="banner-por-evaluar"
          style={{
            background: "linear-gradient(90deg, #FEF3C7 0%, #FDE68A 100%)",
            border: "1px solid #F59E0B",
            borderRadius: 12,
            padding: "16px 20px",
            marginBottom: 20,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div>
            <strong style={{ color: "#92400E", fontSize: 15 }}>
              🙏 Tienes {porEvaluar.length} servicio{porEvaluar.length > 1 ? "s" : ""} por evaluar
            </strong>
            <div style={{ color: "#78350F", fontSize: 13, marginTop: 2 }}>
              Tu opinión nos ayuda a mejorar. Toma menos de 1 minuto.
            </div>
          </div>
          <Link
            to={`/cuenta/historial?evaluar=${porEvaluar[0].id}`}
            style={{ background: "#B45309", color: "white", padding: "10px 18px", borderRadius: 8, textDecoration: "none", fontWeight: 700, fontSize: 14 }}
            data-testid="banner-evaluar-cta"
          >
            Evaluar ahora →
          </Link>
        </div>
      )}

      <section className="cards-row">
        <div className={`stat-card ${statusBadge === "active" ? "stat-ok" : "stat-warn"}`} data-testid="stat-membership">
          <span className="stat-label">Membresía</span>
          <strong className="stat-value">
            {statusBadge === "active" ? "Activa" : "Pendiente"}
          </strong>
          <small>{user?.plan ? `Plan ${user.plan}` : "Sin plan"}</small>
        </div>
        <div className="stat-card" data-testid="stat-beneficiarios">
          <span className="stat-label">Beneficiarios</span>
          <strong className="stat-value">{beneficiariosCount}</strong>
          <Link to="/cuenta/beneficiarios" className="stat-link">Gestionar →</Link>
        </div>
        <div className="stat-card" data-testid="stat-solicitudes">
          <span className="stat-label">Solicitudes</span>
          <strong className="stat-value">{solicitudes.length}</strong>
          <Link to="/cuenta/historial" className="stat-link">Ver historial →</Link>
        </div>
        <div className="stat-card" data-testid="stat-descuentos" style={{ borderColor: "#7DC242", background: "#F6FBEF" }}>
          <span className="stat-label"><i className="fas fa-tags" style={{ color: "#5a9e2f" }} /> App de descuentos</span>
          <strong className="stat-value">{statusBadge === "active" ? "Incluida" : "—"}</strong>
          <small>{statusBadge === "active" ? "Recibirás tu código de activación por correo" : "Se activa con tu membresía"}</small>
        </div>
        <div className="stat-card" data-testid="stat-salud" style={{ borderColor: user?.anura_addon_active ? "#DC2626" : undefined, background: user?.anura_addon_active ? "#FEF2F2" : undefined }}>
          <span className="stat-label"><i className="fas fa-heart-pulse" style={{ color: "#DC2626" }} /> Chequeo de salud</span>
          <strong className="stat-value">{user?.anura_addon_active ? "Activo" : "—"}</strong>
          <Link to="/cuenta/salud" className="stat-link">{user?.anura_addon_active ? "Hacer chequeo →" : "Conocer más →"}</Link>
        </div>
      </section>

      {statusBadge !== "active" && (
        <div className="alert-card" data-testid="alert-activar">
          <i className="fas fa-info-circle" />
          <div>
            <strong>Tu membresía no está activa.</strong>
            <p>{beneficiariosCount === 0 ? "Agrega beneficiarios y elige un plan para activar la servicio." : "Elige un plan para activar la servicio."}</p>
          </div>
          <Link to="/cuenta/onboarding" className="btn btn-primary" data-testid="continue-setup">
            Continuar configuración
          </Link>
        </div>
      )}

      <section className="panel">
        <h2>Solicitudes recientes</h2>
        {solicitudes.length === 0 ? (
          <p className="text-muted">Aún no hay solicitudes.</p>
        ) : (
          <ul className="sol-list">
            {solicitudes.slice(0, 5).map((s) => (
              <li key={s.id} data-testid={`sol-${s.id}`}>
                <span className={`sol-pill sol-${s.status}`}>{s.status}</span>
                <strong>{s.tipo.toUpperCase()}</strong>
                <span className="text-muted">{s.beneficiario?.nombre}</span>
                <small>{new Date(s.created_at).toLocaleString("es-MX")}</small>
              </li>
            ))}
          </ul>
        )}
      </section>
    </AppLayout>
  );
}
